
<template>
  <div>
    <vue-tel-input
      ref="phoneInput"
      class="dropPhone no-shadow"
      v-model="numero"
      :defaultCountry="defaultCountry"
      v-bind="vueTel"
      @input="check"
      @country-changed="handleCountryChange"
      required
    >
    </vue-tel-input>
  </div>
</template>

<script>
import "vue-phone-number-input/dist/vue-phone-number-input.css";
export default {
  data() {
    return {
      numero: null,
      defaultCountry: "FR",
      isUserTyping: false,
      typingTimeout: null,
      isInitialized: false,
      userHasInteracted: false, // Flag pour bloquer toute mise à jour externe après interaction
      vueTel: {
        preferredCountries: ["FR", "TN"],
        placeholder: "Enter your phone",
        mode: "international",
        inputOptions: {
          showDialCode: true,
          tabindex: 0,
        },
        disabledFormatting: false,
        validCharactersOnly: true,
        wrapperClasses: "country-phone-input",
        autoFormat: true,
        dropdownOptions: {
          showFlags: true,
          showSearchBox: true,
        },
      },
    };
  },
  props: {
    tel: Object,
  },
  methods: {
    check(phone, event) {
      // Marquer que l'utilisateur a interagi avec le champ
      this.userHasInteracted = true;
      this.isUserTyping = true;
      clearTimeout(this.typingTimeout);
      this.typingTimeout = setTimeout(() => {
        this.isUserTyping = false;
      }, 2000);
      
      this.$emit("phoneNumber", event);
    },
    handleCountryChange(country) {
      // Mettre à jour le pays quand l'utilisateur change de pays
      if (country && country.iso2) {
        this.defaultCountry = country.iso2.toUpperCase();
      }
    },
    initializePhone() {
      if (!this.tel) {
        this.numero = null;
        this.defaultCountry = "FR";
        this.isInitialized = true;
        return;
      }
      
      // Gérer le cas où tel est une string simple
      if (typeof this.tel === 'string' && this.tel.trim()) {
        // C'est une string, la convertir en objet
        this.defaultCountry = "FR";
        this.numero = this.tel.replace(/[^\d]/g, '').trim();
        this.isInitialized = true;
        return;
      }
      
      // Gérer le cas où tel est un objet
      if (this.tel && (this.tel.phone_number || this.tel.formated_phone || this.tel.phone || this.tel.tel)) {
        const countryCode = this.getCountryCode(this.tel);
        this.defaultCountry = countryCode;
        
        const formatedPhone = this.tel.formated_phone || null;
        // Chercher phone_number dans différentes propriétés possibles
        let phoneNumber = this.tel.phone_number || 
                          (this.tel.phone && this.tel.phone.phone_number) ||
                          this.tel.tel || 
                          '';
        
        // Utiliser phone_number tel quel si disponible (c'est déjà le numéro sans préfixe)
        // Ne PAS nettoyer ou modifier le numéro - le passer tel quel au composant
        if (phoneNumber) {
          // Convertir en string et garder tous les chiffres
          phoneNumber = phoneNumber.toString().trim();
          
          // Si le numéro ne contient pas de préfixe (+), utiliser tel quel
          // Le composant vue-tel-input ajoutera automatiquement le préfixe basé sur defaultCountry
          if (phoneNumber && !phoneNumber.startsWith('+')) {
            // Retirer seulement les espaces et caractères non numériques, mais garder TOUS les chiffres
            this.numero = phoneNumber.replace(/[^\d]/g, '');
          } else if (phoneNumber.startsWith('+')) {
            // Si ça commence par +, extraire le numéro en retirant le préfixe
            this.numero = phoneNumber.replace(/^\+\d{1,4}/, '').replace(/[^\d]/g, '');
          } else {
            // Garder tel quel
            this.numero = phoneNumber.replace(/[^\d]/g, '');
          }
        } else if (formatedPhone) {
          // Si seulement formated_phone est disponible, extraire le numéro
          if (formatedPhone.startsWith('+')) {
            // "+33745665533" -> "745665533" (retirer "+33")
            this.numero = formatedPhone.replace(/^\+\d{1,4}/, '').replace(/[^\d]/g, '');
          } else {
            this.numero = formatedPhone.replace(/[^\d]/g, '');
          }
        } else {
          this.numero = null;
        }
      } else {
        this.numero = null;
        this.defaultCountry = "FR";
      }
      this.isInitialized = true;
    },
    cleanPhoneNumber(phoneNumber, formatedPhone) {
      // Si phone_number est déjà fourni et semble valide, l'utiliser tel quel
      if (phoneNumber) {
        let numStr = phoneNumber.toString().trim();
        if (numStr && numStr.length > 0) {
          // Si le numéro ne commence pas par + et ne contient pas d'espaces de formatage,
          // c'est probablement déjà le bon format (sans préfixe pays)
          // Le composant vue-tel-input gère le préfixe séparément
          if (!numStr.startsWith("+") && !numStr.includes(" ")) {
            // C'est déjà un numéro sans préfixe, le retourner tel quel
            return numStr.replace(/\D/g, '') || null;
          }
        }
      }
      
      if (!phoneNumber && !formatedPhone) return null;
      
      // Convertir en string si ce n'est pas déjà le cas
      let numStr = phoneNumber ? phoneNumber.toString().trim() : '';
      
      // Si formated_phone est disponible, l'utiliser pour extraire le numéro
      if (formatedPhone && typeof formatedPhone === 'string') {
        const formatted = formatedPhone.trim();
        if (formatted.startsWith("+")) {
          // Extraire seulement le numéro sans le préfixe pays
          // Format attendu: +33 1 23 45 67 89 ou +33745665533
          // Retirer le + et le préfixe pays (1-4 chiffres)
          let cleaned = formatted.replace(/^\+\d{1,4}\s*/, "").trim();
          // Retirer tous les espaces et caractères non numériques
          cleaned = cleaned.replace(/\s+/g, "").replace(/[^\d]/g, "");
          return cleaned || null;
        }
        return formatted.replace(/\s+/g, "").replace(/[^\d]/g, "");
      }
      
      // Si phone_number contient déjà le numéro sans préfixe, l'utiliser tel quel
      if (numStr && !numStr.startsWith("+") && !numStr.includes(" ")) {
        // Retirer seulement les caractères non numériques pour garder le numéro intact
        return numStr.replace(/[^\d]/g, "") || null;
      }
      
      // Si le numéro contient un espace, c'est probablement formaté avec préfixe
      if (numStr.includes(" ")) {
        // Si ça commence par +, retirer le préfixe
        if (numStr.startsWith("+")) {
          let cleaned = numStr.replace(/^\+\d{1,4}\s*/, "").trim();
          cleaned = cleaned.replace(/\s+/g, "").replace(/[^\d]/g, "");
          return cleaned || null;
        }
        // Sinon, retirer juste les espaces
        return numStr.replace(/\s+/g, "").replace(/[^\d]/g, "");
      }
      
      // Si le numéro commence par +, retirer le préfixe pays
      if (numStr.startsWith("+")) {
        // Retirer le + et le préfixe pays (1-4 chiffres)
        let cleaned = numStr.replace(/^\+\d{1,4}/, "").trim();
        // Retirer tous les caractères non numériques restants
        cleaned = cleaned.replace(/[^\d]/g, "");
        return cleaned || null;
      }
      
      // Retirer seulement les espaces et caractères non numériques, mais garder tous les chiffres
      return numStr.replace(/[^\d]/g, "") || null;
    },
    getCountryCode(telObj) {
      if (!telObj) return "FR";
      // Chercher le code ISO dans différentes propriétés possibles
      const isoCode = telObj.phone_iso_code || 
                     telObj.iso_code || 
                     (telObj.country_id && telObj.country_id.iso_code) ||
                     "FR";
      
      // Convertir en majuscules pour être sûr que c'est correct
      return isoCode.toUpperCase();
    },
  },
  mounted() {
    this.initializePhone();
  },
  watch: {
    tel: {
      deep: true,
      immediate: false,
      handler(newVal) {
        // BLOQUER COMPLÈTEMENT toute mise à jour si l'utilisateur a interagi
        if (this.userHasInteracted) {
          return; // Ne jamais modifier le numéro une fois que l'utilisateur a tapé
        }
        
        // Protection supplémentaire pendant la saisie
        if (this.isUserTyping) {
          return;
        }
        
        // Initialisation uniquement si pas encore fait
        if (!this.isInitialized) {
          this.initializePhone();
          return;
        }
        
        // Si tel devient null, réinitialiser seulement si pas d'interaction utilisateur
        if (!newVal && !this.userHasInteracted) {
          this.numero = null;
          this.defaultCountry = "FR";
          return;
        }
        
        // Ne mettre à jour que si le numéro est complètement vide et que l'utilisateur n'a pas interagi
        if (!this.userHasInteracted && 
            (!this.numero || this.numero === null || this.numero === '') && 
            newVal && (newVal.phone_number || newVal.formated_phone)) {
          const countryCode = this.getCountryCode(newVal);
          if (countryCode && this.defaultCountry !== countryCode) {
            this.defaultCountry = countryCode;
          }
          
          const formatedPhone = newVal.formated_phone || null;
          const phoneNumber = newVal.phone_number || '';
          
          // Si phone_number est déjà fourni et semble être un numéro valide (sans préfixe),
          // l'utiliser directement sans nettoyage supplémentaire
          let cleanedNumber;
          if (phoneNumber && typeof phoneNumber === 'string' && phoneNumber.trim() && !phoneNumber.startsWith('+') && !phoneNumber.includes(' ')) {
            // C'est déjà un numéro sans préfixe, utiliser tel quel
            cleanedNumber = phoneNumber.replace(/[^\d]/g, '');
          } else {
            // Sinon, nettoyer le numéro
            cleanedNumber = this.cleanPhoneNumber(phoneNumber, formatedPhone);
          }
          
          if (cleanedNumber) {
            this.numero = cleanedNumber;
          }
        }
      },
    },
    numero(newVal) {
      // Si le numéro change (même juste un caractère), marquer l'interaction
      if (newVal !== null && newVal !== '') {
        this.userHasInteracted = true; // Marquer définitivement l'interaction
        this.isUserTyping = true;
        clearTimeout(this.typingTimeout);
        this.typingTimeout = setTimeout(() => {
          this.isUserTyping = false;
        }, 5000);
      } else if (newVal === null || newVal === '') {
        // Si le numéro devient complètement vide, permettre les mises à jour externes
        // Mais seulement après un délai pour éviter les conflits
        clearTimeout(this.typingTimeout);
        this.typingTimeout = setTimeout(() => {
          this.isUserTyping = false;
          // Si le champ est resté vide, réautoriser les mises à jour externes
          if (!this.numero || this.numero === null || this.numero === '') {
            this.userHasInteracted = false;
          }
        }, 1000);
      }
    },
    defaultCountry(newVal) {
      if (!newVal) {
        this.defaultCountry = "FR";
      }
    },
  },
  beforeDestroy() {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
  },
};
</script>

<style scoped>
::v-deep input {
  box-shadow: none !important;
}

.vue-tel-input[data-v-48307be2][data-v-50294cc3] {
    border-radius: 3px;
    display: flex;
    text-align: left;
    box-shadow: none !important;
    border-color: #ced4da !important;
    height: 41.1px !important;
    border-radius: 5px !important;
}
::v-deep .dropdown.open {
  z-index: 20 !important;
  box-shadow: none !important;
 
}
::v-deep ul[data-v-48307be2] {
    z-index: 1;
    padding: 0;
    margin: 0;
    text-align: left;
    list-style: none;
    max-height: 200px;
    overflow-y: scroll;
    position: absolute;
    top: 33px;
    left: -1px;
    background-color: #fff;
    border: 1px solid #ccc;
    width: 375px !important;
    overflow-x: clip !important;
}
.dropdown.open {
    z-index: 20 !important;
}
::v-deep  vue-tel-input country-phone-input{
  box-shadow: none !important;
}
.no-shadow .country-phone-input input {
  box-shadow: none !important;
}
.country-phone-input input {
  box-shadow: none !important;
}
.vue-tel-input[data-v-48307be2] {
    border-radius: 3px;
    display: flex;
    text-align: left;
    box-shadow: none !important;
    border-color: #ced4da !important;
}
/* S'assurer que le sélecteur de pays (code ISO) est toujours visible */
::v-deep .vue-tel-input .vti__dropdown {
    display: flex !important;
    visibility: visible !important;
    opacity: 1 !important;
    pointer-events: auto !important;
}
::v-deep .vue-tel-input .vti__flag {
    display: block !important;
    visibility: visible !important;
}
::v-deep .vue-tel-input .vti__selection {
    display: flex !important;
    visibility: visible !important;
    min-width: 50px;
}
/* S'assurer que le code de pays est toujours affiché dans l'input */
::v-deep .vue-tel-input .vti__input {
    padding-left: 70px !important;
}
::v-deep .vue-tel-input .vti__input.has-value ~ .vti__selection {
    display: flex !important;
}
</style>
